// Hardware bridges for browser ↔ CX1500N + similar UHF handhelds.
//
// 1. Trigger button: hardware F1 fires KeyboardEvent.code='F1' or keyCode 112,
//    plus vendor 292 (CX1500N). We dispatch a custom 'identium-trigger' event
//    so pages can listen without owning global keydown.
//
// 2. Battery: navigator.getBattery() works on Android Chrome.
//
// 3. Connection signal: navigator.connection (effectiveType + downlink + rtt).
//
// All helpers degrade gracefully on browsers that lack the APIs.

// ── 1. Trigger button ──────────────────────────────────────────────────────
let triggerInstalled = false;
const TRIGGER_KEYCODES = new Set([112, 292]);   // F1, vendor F1
const TRIGGER_CODES = new Set(['F1']);

export function installTriggerHandler() {
  if (triggerInstalled) return;
  triggerInstalled = true;

  const handler = (e) => {
    if (TRIGGER_CODES.has(e.code) || TRIGGER_KEYCODES.has(e.keyCode)) {
      // Avoid stomping the browser's own F1 (Help) on desktop only when
      // we have an active listener. We always preventDefault to be safe.
      e.preventDefault();
      window.dispatchEvent(new CustomEvent('identium-trigger', {
        detail: { source: 'hardware', code: e.code, keyCode: e.keyCode },
      }));
    }
  };
  window.addEventListener('keydown', handler);
  return () => {
    window.removeEventListener('keydown', handler);
    triggerInstalled = false;
  };
}

// Subscribe to trigger events
export function onTrigger(handler) {
  const wrapped = (e) => handler(e.detail);
  window.addEventListener('identium-trigger', wrapped);
  return () => window.removeEventListener('identium-trigger', wrapped);
}

// ── 2. Battery ──────────────────────────────────────────────────────────────
// Returns a stable BatteryStatus object that updates as the device reports
// changes. Returns null if API not available.
export async function watchBattery(callback) {
  if (!('getBattery' in navigator)) {
    callback({ supported: false, level: null, charging: null });
    return () => {};
  }
  try {
    const battery = await navigator.getBattery();
    const fire = () => callback({
      supported: true,
      level: Math.round(battery.level * 100),
      charging: battery.charging,
      chargingTime: battery.chargingTime,
      dischargingTime: battery.dischargingTime,
    });
    fire();
    battery.addEventListener('levelchange', fire);
    battery.addEventListener('chargingchange', fire);
    return () => {
      battery.removeEventListener('levelchange', fire);
      battery.removeEventListener('chargingchange', fire);
    };
  } catch {
    callback({ supported: false, level: null, charging: null });
    return () => {};
  }
}

// ── 3. Network signal ───────────────────────────────────────────────────────
// Returns { online, effectiveType, downlink, rtt, bars } where bars is 0..5.
function deriveBars({ online, effectiveType }) {
  if (online === false) return 0;
  switch (effectiveType) {
    case '4g':       return 5;
    case '3g':       return 3;
    case '2g':       return 2;
    case 'slow-2g':  return 1;
    default:         return online === true ? 4 : 0;  // unknown but online → 4 bars
  }
}

export function watchSignal(callback) {
  const c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  const fire = () => {
    const status = {
      online: navigator.onLine,
      effectiveType: c?.effectiveType,
      downlink: c?.downlink,
      rtt: c?.rtt,
      type: c?.type,
    };
    status.bars = deriveBars(status);
    callback(status);
  };
  fire();
  if (c) c.addEventListener('change', fire);
  window.addEventListener('online', fire);
  window.addEventListener('offline', fire);
  return () => {
    if (c) c.removeEventListener('change', fire);
    window.removeEventListener('online', fire);
    window.removeEventListener('offline', fire);
  };
}
